import { cn } from "../utils/cn";
import v from "./traVisual.module.css";

/**
 * Normalize rankings `recentForm` or a letter array into W/L/— slots.
 * @param {{ results?: string[]; label?: string } | null | undefined} form
 * @param {string[] | null | undefined} letters
 * @param {number} limit
 * @returns {('W'|'L'|'—')[]}
 */
export function resolveFormLetters(form, letters, limit = 5) {
  const fromForm =
    Array.isArray(form?.results) && form.results.length > 0 ? form.results : null;
  const raw = (fromForm || letters || [])
    .map((x) => {
      const s = String(x || "").toUpperCase();
      if (s === "W" || s === "L") return s;
      return null;
    })
    .filter(Boolean)
    .slice(0, limit);
  return /** @type {('W'|'L'|'—')[]} */ (raw);
}

/**
 * Canonical recent-form visualization — slanted W/L pill bars + mono letters + “Last N”.
 * Golden reference: App_screenshots “How you’re playing” / last-5 form.
 *
 * Use on Me, Player Home, career overview, rankings profile — never invent a second form UI.
 *
 * @param {object} props
 * @param {string[]} [props.letters] newest-first W/L
 * @param {{ results?: string[]; label?: string }} [props.form] rankings-style object
 * @param {number} [props.limit=5]
 * @param {boolean} [props.padEmpty=true] show dashed placeholders when no results
 * @param {string} [props.caption] defaults to `Last ${limit}`
 * @param {string} [props.className]
 * @param {import('react').ReactNode} [props.empty] when padEmpty is false and no data
 */
export default function TRARecentFormBars({
  letters,
  form,
  limit = 5,
  padEmpty = true,
  caption,
  className,
  empty = null,
}) {
  const resolved = resolveFormLetters(form, letters, limit);
  const slots =
    resolved.length > 0
      ? resolved
      : padEmpty
        ? Array.from({ length: limit }, () => "—")
        : [];

  if (!slots.length) {
    return empty || <p className={v.formBarsEmpty}>No recent matches</p>;
  }

  const label = (caption || `Last ${slots.length === limit ? limit : slots.length}`).toUpperCase();
  const aria =
    resolved.length > 0
      ? `Last ${resolved.length} results: ${resolved.join(" ")}`
      : "No recent results";

  return (
    <div className={cn(v.formBars, className)} aria-label={aria} data-testid="tra-recent-form-bars">
      {slots.map((letter, i) => {
        const isLossish = letter === "L" || letter === "—";
        return (
          <div key={`form-${i}-${letter}`} className={v.formBarCol}>
            <div
              className={cn(v.formBar, isLossish && v.formBarLoss)}
              aria-hidden
            />
            <span className={cn(v.formBarLetter, isLossish && v.formBarLetterLoss)}>{letter}</span>
          </div>
        );
      })}
      <div className={v.formSpacer} />
      <span className={v.formBarsCaption}>{label}</span>
    </div>
  );
}
